import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, AlertCircle, Loader2 } from 'lucide-react';
import { useProjects } from '../hooks/useApi';
import { checkApiHealth } from '../services/api';

const ApiStatus: React.FC = () => {
  const [healthStatus, setHealthStatus] = useState<'healthy' | 'unhealthy' | 'checking'>('checking');
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Use cached hook instead of direct API call
  const { data: projects, loading: projectsLoading, error: projectsError } = useProjects();

  // Check API health
  const checkHealth = async () => {
    try {
      setHealthStatus('checking');
      const isHealthy = await checkApiHealth();
      setHealthStatus(isHealthy ? 'healthy' : 'unhealthy');
      setLastChecked(new Date());
      setError(null);
    } catch (err) {
      setHealthStatus('unhealthy');
      setError(err instanceof Error ? err.message : 'Health check failed');
      setLastChecked(new Date());
    }
  };

  // Check health on mount and when projects change
  useEffect(() => {
    checkHealth();
  }, [projects]);

  // Handle errors from cached hooks
  useEffect(() => {
    if (projectsError) {
      setError(projectsError);
    }
  }, [projectsError]);

  const getStatusIcon = () => {
    switch (healthStatus) {
      case 'healthy':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'unhealthy':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'checking':
        return <Loader2 className="w-5 h-5 text-yellow-500 animate-spin" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusText = () => {
    switch (healthStatus) {
      case 'healthy':
        return 'API Healthy';
      case 'unhealthy':
        return 'API Unhealthy';
      case 'checking':
        return 'Checking...';
      default:
        return 'Unknown Status';
    }
  };

  const getStatusColor = () => {
    switch (healthStatus) {
      case 'healthy':
        return 'text-green-600 dark:text-green-400';
      case 'unhealthy':
        return 'text-red-600 dark:text-red-400';
      case 'checking':
        return 'text-yellow-600 dark:text-yellow-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  return (
    <div className="flex items-center space-x-3 p-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      {getStatusIcon()}
      <div className="flex-1">
        <div className={`font-medium ${getStatusColor()}`}>
          {getStatusText()}
        </div>
        {lastChecked && (
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Last checked: {lastChecked.toLocaleTimeString()}
          </div>
        )}
        {error && (
          <div className="text-xs text-red-500 dark:text-red-400 mt-1">
            {error}
          </div>
        )}
        {projects && (
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {projects.length} projects loaded
          </div>
        )}
      </div>
      <button
        onClick={checkHealth}
        disabled={healthStatus === 'checking'}
        className="px-3 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Refresh
      </button>
    </div>
  );
};

export default ApiStatus;
